import { useCallback } from "react";
import { partialUpdateFiling } from "../../../../../../api/filings/filingAPI";
import { fetchData } from "pdfjs-dist";
import { generateCryptoGainLoss } from "../../../../../../api/filings/documentsAPI";
import { checkFilingReview } from "../../../../../../api/filings/reviewAPI";
import { Handler401 } from "../../../../../../utils/auth/authHandler";

export function useFilingUI({
  modalState,
  filingActions,
  filingData,
  documentActions,
  isCpa,
  setPendingFeedbackType,
  filingUuid,
  filing,
  navigate,
  setHasCheckedReview,
  setShowDemoReviewModal,
  setShowCusReviewIntroModal,
  setEditingDependentIndex,
  setDeleteDependentIndex,
  setIsDependentModalOpen,
}) {
  const {
    setIsUploadModalOpen,
    setSelectedSource,
    setSelectedDocumentId,
    setShow8879PrepareModal,
    setShowRequestSignatureModal,
    setShowSignAuthModal,
    setShowDeleteConfirmation,
    setShowEnableUploadConfirmation,
    setShowGenerateGainLossModal,
    setSelectedGainLossYear,
    setGainLossMethod,
    setShowProcessCompleteModal,
    setIsSigningModalOpen,
  } = modalState;

  const {
    setPrepare8879Error,
    setRequestSignatureError,
    setRequestSignatureSuccess,
    setIsRequestingSignature,
    setSignAuthError,
    setIsLoadingSignAuth,
    setIsPreparing8879,
  } = filingActions;

  // --- Modal Opening Handlers ---
  const handleOpenUploadModal = useCallback(
    (source, documentId = null) => {
      // IF THE GHOST APPEARS, PAUSE THE BROWSER
      if (source?.name === "EFILE_SIGNATURE") {
        debugger;
      }
      setSelectedSource(source);
      setSelectedDocumentId(documentId);
      setIsUploadModalOpen(true);
    },
    [setSelectedSource, setSelectedDocumentId, setIsUploadModalOpen],
  );

  const handleOpenGenerateGainLossModal = useCallback(() => {
    const ptfSituation =
      filingData.filing.tax_situation?.details?.PTF?.situation;
    const taxYears = ptfSituation?.taxYears;
    const taxYearsArray = Array.isArray(taxYears)
      ? taxYears
      : taxYears
      ? [taxYears]
      : [];

    if (taxYearsArray.length > 0) {
      setSelectedGainLossYear(taxYearsArray[0]);
    }
    setShowGenerateGainLossModal(true);
  }, [
    filingData.filing,
    setSelectedGainLossYear,
    setShowGenerateGainLossModal,
  ]);

  // --- Modal Closing Handlers ---
  const handleCloseUploadModal = useCallback(() => {
    setIsUploadModalOpen(false);
    setSelectedSource(null);
    setSelectedDocumentId(null);
  }, [setIsUploadModalOpen, setSelectedSource, setSelectedDocumentId]);

  const handleClose8879PrepareModal = useCallback(() => {
    setShow8879PrepareModal(false);
    setPrepare8879Error(null);
    setIsPreparing8879(false);
  }, [setShow8879PrepareModal, setPrepare8879Error, setIsPreparing8879]);

  const handleCloseRequestSignatureModal = useCallback(() => {
    setShowRequestSignatureModal(false);
    setRequestSignatureError(null);
    setRequestSignatureSuccess(false);
    setIsRequestingSignature(false);
  }, [
    setShowRequestSignatureModal,
    setRequestSignatureError,
    setRequestSignatureSuccess,
    setIsRequestingSignature,
  ]);

  const handleCloseSignAuthModal = useCallback(() => {
    setShowSignAuthModal(false);
    setSignAuthError(null);
    setIsLoadingSignAuth(false);
  }, [setShowSignAuthModal, setSignAuthError, setIsLoadingSignAuth]);

  // --- Success Flows ---
  const handleUploadSuccess = useCallback(
    async (result) => {
      await filingData.fetchDocuments();
      handleCloseUploadModal();

      if (result?.summary_metrics) {
        filingData.setFiling((prev) => ({
          ...prev,
          summary_metrics: result.summary_metrics,
        }));
      }

      if (isCpa && result?.processStatus === "COM") {
        setPendingFeedbackType(result.mode === "DE" ? "demo" : "regular");
        setShowProcessCompleteModal(true);
      }
    },
    [
      filingData,
      handleCloseUploadModal,
      isCpa,
      setPendingFeedbackType,
      setShowProcessCompleteModal,
    ],
  );

  // Dependent management functions
  const handleAddDependent = () => {
    setEditingDependentIndex(null);
    setIsDependentModalOpen(true);
  };

  const handleEditDependent = (index) => {
    setEditingDependentIndex(index);
    setIsDependentModalOpen(true);
  };

  const handleDeleteDependent = (index) => {
    setDeleteDependentIndex(index);
    setShowDeleteConfirmation(true);
  };

  const handleCancelDeleteDependent = () => {
    setDeleteDependentIndex(null);
    setShowDeleteConfirmation(false);
  };

  const handleCloseDependentModal = () => {
    setIsDependentModalOpen(false);
    setEditingDependentIndex(null);
  };

  // Handle opening Request Signature confirmation modal
  const handleOpenRequestSignatureModal = () => {
    setRequestSignatureError(null);
    setRequestSignatureSuccess(false);
    setIsRequestingSignature(false);
    setShowRequestSignatureModal(true);
  };

  // Handle enabling crypto uploads (show confirmation first)
  const handleEnableCryptoUpload = () => {
    setShowEnableUploadConfirmation(true);
  };

  const handleEfileStateUpdate = async (updateData) => {
    try {
      const response = await partialUpdateFiling(
        "tax/filing",
        { expert_documents_state_update: updateData },
        filingUuid,
      );
      if (response.ok) {
        await fetchData(); // Refresh main data
        return true;
      }
      // Handle errors (alert/toast) here
      return false;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  const onGenerateGainLossSubmit = async (year, method) => {
    try {
      const response = await generateCryptoGainLoss(year, filingUuid, method);
      if (response.ok) {
        await filingData.fetchDocuments();
        await fetchData();
        return { success: true };
      }
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        message: errorData.message || "Failed to generate report.",
      };
    } catch (err) {
      return { success: false, message: "An error occurred." };
    }
  };

  // Check if user has already submitted a review and show modal if needed
  const checkAndShowReviewModal = async () => {
    if (!filingUuid) return;

    try {
      let reviewExists = true;

      try {
        const response = await checkFilingReview(filingUuid);

        if (response.ok) {
          const data = await response.json();
          // Check if reviews is None or if there's actual review data
          if (data.review === null) {
            reviewExists = false;
          }
        } else {
          // For 404 or any other status, assume no review exists
          reviewExists = false;
        }
      } catch (fetchErr) {
        console.error("Error fetching reviews:", fetchErr);
        // If we can't check, assume no review exists to show the modal
        reviewExists = false;
      }

      setHasCheckedReview(true);

      // Show review modal if filing is complete and no review exists
      // Also check if user already skipped the review
      if (!reviewExists && !filing.review_skiped) {
        if (isCpa) {
          setShowDemoReviewModal(true);
        } else {
          setShowCusReviewIntroModal(true); // Show intro modal first for CUS
        }
      }
    } catch (err) {
      setHasCheckedReview(true);
      // On error, don't show modal to avoid annoying users
    }
  };

  const handleUploadError = (error) => {
    console.error("Upload error:", error);
    // Error is handled by the modal
  };

  // Actually perform the enable upload action after confirmation
  const handleConfirmEnableCryptoUpload = async () => {
    if (!filingUuid) {
      setShowEnableUploadConfirmation(false);
      return;
    }

    try {
      // Use PATCH to update only the crypto_upload_completed field
      const response = await partialUpdateFiling(
        "tax/filing",
        {
          crypto_upload_completed: false,
        },
        filingUuid,
      );

      if (response.status === 401) {
        Handler401(navigate);
        setShowEnableUploadConfirmation(false);
        return;
      }

      if (response.ok) {
        // Refresh filing data
        await fetchData();
        setShowEnableUploadConfirmation(false);
      } else {
        const errorData = await response.json().catch(() => ({}));
        alert(
          `Failed to enable crypto uploads: ${
            errorData.message || response.statusText || "Unknown error"
          }`,
        );
        setShowEnableUploadConfirmation(false);
      }
    } catch (err) {
      console.error("Error enabling crypto uploads:", err);
      alert("Failed to enable crypto uploads. Please try again.");
      setShowEnableUploadConfirmation(false);
    }
  };

  // Cancel enable upload confirmation
  const handleCancelEnableCryptoUpload = () => {
    setShowEnableUploadConfirmation(false);
  };

  // Handle e-signature completion
  const handleEfileSignatureCompleted = async () => {
    // Refresh documents to show the signed version
    await filingData.fetchDocuments();
    setIsSigningModalOpen(false);
  };

  return {
    handleOpenUploadModal,
    handleCloseUploadModal,
    handleOpenGenerateGainLossModal,
    handleClose8879PrepareModal,
    handleCloseRequestSignatureModal,
    handleCloseSignAuthModal,
    handleUploadSuccess,
    handleAddDependent,
    handleEditDependent,
    handleDeleteDependent,
    handleCancelDeleteDependent,
    handleCloseDependentModal,
    handleOpenRequestSignatureModal,
    handleEnableCryptoUpload,
    handleEfileStateUpdate,
    onGenerateGainLossSubmit,
    checkAndShowReviewModal,
    handleUploadError,
    handleConfirmEnableCryptoUpload,
    handleCancelEnableCryptoUpload,
    handleEfileSignatureCompleted,
  };
}
